// Frontend: ProjectForm.jsx
import React, { useState } from 'react';
import Button from '../UI/Button';

const ProjectForm = ({ onSubmit, loading = false }) => {
  const [formData, setFormData] = useState({
    name: 'My Awesome Project',
    description: 'A project generated by AI',
    framework: 'python',
    prompt: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  // 🌟 UPDATED: Add combined framework examples
  const frameworkExamples = {
    python: "Create a Python web scraper that extracts data from websites and saves to CSV",
    django: "Build a Django e-commerce site with user authentication and product management",
    flask: "Create a Flask REST API for a todo application with user authentication",
    react: "Build a React dashboard with charts, user management, and dark mode",
    nodejs: "Create a Node.js chat application with WebSocket support",
    express: "Build an Express.js REST API with MongoDB integration and JWT auth",
    vanilla: "Build a portfolio website with HTML, CSS, and JavaScript",

    // 🌟 NEW: Combined framework examples
    'react-python': "Build a React frontend with Python FastAPI backend for a task management app",
    'react-django': "Create a React SPA with Django REST Framework backend for a blogging platform",
    'react-flask': "Develop React dashboard with Flask backend for data visualization",
    'react-node': "Build a fullstack MERN application for a social media platform"
  };

  const setExamplePrompt = () => {
    setFormData({
      ...formData,
      prompt: frameworkExamples[formData.framework] || "Create a complete working application with..."
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Project Name *
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            value={formData.name}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label htmlFor="framework" className="block text-sm font-medium text-gray-700 mb-2">
            Technology Stack *
          </label>
          <select
            id="framework"
            name="framework"
            required
            value={formData.framework}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {/* Backend Only */}
            <optgroup label="Backend Only">
              <option value="python">Python</option>
              <option value="django">Django</option>
              <option value="flask">Flask</option>
              <option value="nodejs">Node.js</option>
              <option value="express">Express.js</option>
            </optgroup>

            {/* Frontend Only */}
            <optgroup label="Frontend Only">
              <option value="react">React</option>
              <option value="vanilla">HTML/CSS/JS</option>
            </optgroup>

            {/* 🌟 NEW: Full Stack */}
            <optgroup label="Full Stack">
              <option value="react-python">React + Python</option>
              <option value="react-django">React + Django</option>
              <option value="react-flask">React + Flask</option>
              <option value="react-node">React + Node.js</option>
            </optgroup>
          </select>

          {/* Framework Description */}
          <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-700">
              {getFrameworkDescription(formData.framework)}
            </p>
          </div>
        </div>
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
          Project Description
        </label>
        <textarea
          id="description"
          name="description"
          rows={3}
          value={formData.description}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="prompt" className="block text-sm font-medium text-gray-700">
            Project Requirements *
          </label>
          <button
            type="button"
            onClick={setExamplePrompt}
            className="text-sm text-blue-600 hover:text-blue-500 hover:underline"
          >
            Use {getFrameworkName(formData.framework)} Example
          </button>
        </div>
        <textarea
          id="prompt"
          name="prompt"
          required
          rows={8}
          value={formData.prompt}
          onChange={handleChange}
          placeholder={`Describe your ${getFrameworkName(formData.framework)} project in detail...`}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <p className="mt-1 text-sm text-gray-500">
          {getPromptHint(formData.framework)}
        </p>
      </div>

      <Button
        type="submit"
        loading={loading}
        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
      >
        🚀 Generate {getFrameworkName(formData.framework)} Project
      </Button>
    </form>
  );
};

// 🌟 NEW: Helper functions for framework descriptions
const getFrameworkDescription = (framework) => {
  const descriptions = {
    python: "Pure Python application with no framework",
    django: "Django web framework with batteries included",
    flask: "Lightweight Flask microframework",
    react: "React single-page application",
    nodejs: "Node.js server application",
    express: "Express.js web framework",
    vanilla: "Vanilla HTML, CSS, and JavaScript",
    'react-python': "React frontend with Python backend (Fullstack)",
    'react-django': "React frontend with Django REST backend (Fullstack)",
    'react-flask': "React frontend with Flask REST backend (Fullstack)",
    'react-node': "React frontend with Node.js backend (MERN-like)"
  };
  return descriptions[framework] || "Select a technology stack";
};

const getFrameworkName = (framework) => {
  const names = {
    python: "Python",
    django: "Django",
    flask: "Flask",
    react: "React",
    nodejs: "Node.js",
    express: "Express.js",
    vanilla: "HTML/CSS/JS",
    'react-python': "React + Python",
    'react-django': "React + Django",
    'react-flask': "React + Flask",
    'react-node': "React + Node.js"
  };
  return names[framework] || "Project";
};

const getPromptHint = (framework) => {
  if (framework.includes('react-')) {
    return "Describe both frontend features and backend API endpoints for your fullstack application";
  }
  return "The more detailed your prompt, the better the results!";
};

export default ProjectForm;